import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useState } from 'react';

import type { StorySlide } from '@sorare/core/src/components/Story';
import { Story } from '@sorare/core/src/components/Story';
import { usePausableStory } from '@sorare/core/src/components/Story/usePausableStory';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { ChooseYourRewardStory } from 'components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory';
import { RewardUnlockedStory } from 'components/missions/stories/RewardUnlockedStory';
import { useClaimTask } from 'components/missions/useClaimTask';

import { ClaimYourStreakRewardsStory } from './ClaimYourStreakRewardsStory';
import type { ThresholdsStreakTaskClaimStory_thresholdsStreakTask } from './__generated__/index.graphql';

type Props = {
  task: ThresholdsStreakTaskClaimStory_thresholdsStreakTask;
  onClose: () => void;
};
export const ThresholdsStreakTaskClaimStory = ({ task, onClose }: Props) => {
  const { pause, handleOnPause, handleOnUnpause } = usePausableStory();
  const [claimTask] = useClaimTask();
  const { sport } = useSportContext();
  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  const { isMobileApp, postMessage } = useIsMobileApp();

  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;

  return (
    <Story
      pause={pause}
      handleOnPause={handleOnPause}
      handleOnUnpause={handleOnUnpause}
      goBackToBeginning={false}
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      stories={[
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <Fragment key="claim-streak-rewards-story">
              <ClaimYourStreakRewardsStory
                task={task}
                onContinue={async () => {
                  if (!canChooseAlternativeRewardConfigs) {
                    await claimTask({
                      variables: {
                        taskId: task.id,
                        sport,
                      },
                    });
                  }
                  onNext();
                }}
              />
            </Fragment>
          ),
        },
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                duration: 2,
                withBackChevron: false,
                withNextChevron: false,
                autoPlayNext: false,
                bodyElement: ({ onNext }) => (
                  <Fragment key="choose-reward-story">
                    <ChooseYourRewardStory
                      rewardConfigs={rewardConfigs}
                      alternativeRewardConfigs={alternativeRewardConfigs}
                      setSignedAlternativeRewardConfigs={
                        setSignedAlternativeRewardConfigs
                      }
                      onConfirm={async () => {
                        await claimTask({
                          variables: {
                            taskId: task.id,
                            signedAlternativeRewardConfigs,
                            sport,
                          },
                        });
                        onNext();
                      }}
                    />
                  </Fragment>
                ),
              } as StorySlide,
            ]
          : []),
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              <RewardUnlockedStory
                open
                onClose={onNext}
                task={task}
                alternativeRewardConfigs={
                  signedAlternativeRewardConfigs
                    ? alternativeRewardConfigs[0].rewardConfigs
                    : undefined
                }
              />
            </Fragment>
          ),
        },
      ]}
    />
  );
};

ThresholdsStreakTaskClaimStory.fragments = {
  taskInterface: gql`
    fragment ThresholdsStreakTaskClaimStory_thresholdsStreakTask on ThresholdsStreakTask {
      id
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
      }
      ...ClaimYourStreakRewardsStory_taskInterface
      ...RewardUnlockedStory_taskInterface
    }
    ${ClaimYourStreakRewardsStory.fragments.taskInterface}
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.taskInterface}
  ` as TypedDocumentNode<ThresholdsStreakTaskClaimStory_thresholdsStreakTask>,
};
